import React, { useMemo, useState } from "react";
import axios from "axios";
import { Link as Linker, useSearchParams, useNavigate } from "react-router-dom";
import { Alert } from "flowbite-react";
import { HiInformationCircle } from "react-icons/hi";
import { HiOutlineLockClosed } from "react-icons/hi";
import { API_BASE_URL } from "../config";

const MIN_PASSWORD_LEN = 8;

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const token = useMemo(() => searchParams.get("token") || "", [searchParams]);

  const [password, setPassword] = useState("");
  const [password2, setPassword2] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState("Password updated. Please sign in.");

  const handleSubmit = async (e) => {
    e.preventDefault();

    setShowError(false);
    setErrorMessage("");
    setShowSuccess(false);

    if (!token) {
      setErrorMessage("Token missing. Please request a new reset link.");
      setShowError(true);
      return;
    }

    if (!password || !password2) {
      setErrorMessage("Please enter your new password twice.");
      setShowError(true);
      return;
    }

    if (password.length < MIN_PASSWORD_LEN) {
      setErrorMessage(`Password must be at least ${MIN_PASSWORD_LEN} characters.`);
      setShowError(true);
      return;
    }

    if (password !== password2) {
      setErrorMessage("Passwords do not match.");
      setShowError(true);
      return;
    }

    setIsSubmitting(true);

    try {
      const res = await axios.post(`${API_BASE_URL}/api/reset_password`, {
        token: token,
        password: password,
        password_confirmation: password2,
      });

      const msg = res?.data?.message || "Password updated. Please sign in.";
      setSuccessMessage(msg);
      setShowSuccess(true);

      // Optional: redirect after success
      setTimeout(() => navigate("/signin"), 1200);
    } catch (err) {
      const status = err?.response?.status;
      const data = err?.response?.data;

      const msg =
        data?.message ||
        data?.error ||
        (Array.isArray(data?.errors) ? data.errors.join(", ") : null) ||
        "Link expired. Please request a new reset link.";

      // If backend returns 422 for invalid/expired token, show it
      if (status === 422) {
        setErrorMessage(msg);
        setShowError(true);
      } else {
        setErrorMessage("Something went wrong. Please try again.");
        setShowError(true);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const tokenMissing = !token;

  return (
    <div className="min-h-screen flex items-center justify-center px-4 bg-gradient-to-b from-[#1896b9]/10 to-white">
      <div className="w-full max-w-md">
        {/* Brand */}
        <div className="mb-4 text-center">
          <Linker to="/" className="hover:bg-transparent hover:before:w-0 inline-block">
            <div className="text-[#1896b9] font-extrabold text-2xl tracking-tight">
              EduBahamas
            </div>
          </Linker>
          <div className="text-sm text-white mt-1">
            Create a new password securely.
          </div>
        </div>

        {/* Card */}
        <div className="bg-white rounded-2xl p-6 sm:p-7 shadow-sm ring-1 ring-black/5">
          <div className="mb-5">
            <h1 className="text-gray-900 text-xl font-semibold">
              Reset your password
            </h1>
            <p className="text-gray-500 text-sm mt-1">
              Enter a new password below.
            </p>
          </div>

          {tokenMissing && (
            <Alert color="failure" icon={HiInformationCircle} className="mb-4">
              Token missing. Please request a new reset link.
            </Alert>
          )}

          {showError && (
            <Alert
              color="failure"
              icon={HiInformationCircle}
              onDismiss={() => setShowError(false)}
              className="mb-4"
            >
              {errorMessage}
            </Alert>
          )}

          {showSuccess && (
            <Alert
              color="success"
              icon={HiInformationCircle}
              onDismiss={() => setShowSuccess(false)}
              className="mb-4"
            >
              {successMessage}
            </Alert>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* New password */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                New password
              </label>

              <div className="relative">
                <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-gray-400">
                  <HiOutlineLockClosed className="h-5 w-5" />
                </span>

                <input
                  className={[
                    "w-full rounded-xl bg-gray-50 border px-3 py-3 pl-11 text-gray-900",
                    "placeholder:text-gray-400",
                    "shadow-sm outline-none transition",
                    "focus:bg-white focus:border-[#1896b9] focus:ring-4 focus:ring-[#1896b9]/20",
                    showError ? "border-red-300" : "border-gray-200",
                  ].join(" ")}
                  type="password"
                  placeholder={`At least ${MIN_PASSWORD_LEN} characters`}
                  value={password}
                  autoComplete="new-password"
                  onChange={(e) => {
                    setPassword(e.target.value);
                    setShowError(false);
                    setShowSuccess(false);
                  }}
                  disabled={tokenMissing}
                />
              </div>

              <p className="mt-2 text-xs text-gray-500">
                Use at least {MIN_PASSWORD_LEN} characters.
              </p>
            </div>

            {/* Confirm password */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Confirm new password
              </label>

              <div className="relative">
                <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-gray-400">
                  <HiOutlineLockClosed className="h-5 w-5" />
                </span>

                <input
                  className={[
                    "w-full rounded-xl bg-gray-50 border px-3 py-3 pl-11 text-gray-900",
                    "placeholder:text-gray-400",
                    "shadow-sm outline-none transition",
                    "focus:bg-white focus:border-[#1896b9] focus:ring-4 focus:ring-[#1896b9]/20",
                    showError ? "border-red-300" : "border-gray-200",
                  ].join(" ")}
                  type="password"
                  placeholder="Re-type your new password"
                  value={password2}
                  autoComplete="new-password"
                  onChange={(e) => {
                    setPassword2(e.target.value);
                    setShowError(false);
                    setShowSuccess(false);
                  }}
                  disabled={tokenMissing}
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={isSubmitting || tokenMissing}
              className={[
                "w-full rounded-xl py-3 font-semibold text-white",
                "bg-[#1896b9] hover:bg-[#126e87]",
                "shadow-sm transition",
                "focus:outline-none focus:ring-4 focus:ring-[#1896b9]/25",
                "disabled:opacity-60 disabled:cursor-not-allowed",
                "flex items-center justify-center",
              ].join(" ")}
            >
              {isSubmitting && (
                <span className="mr-2 inline-block h-4 w-4 animate-spin rounded-full border-2 border-white/50 border-t-white" />
              )}
              {isSubmitting ? "Updating..." : "Update Password"}
            </button>

            <div className="pt-1 text-center">
              <Linker
                to="/signin"
                className="text-sm text-gray-600 hover:text-gray-900 hover:underline hover:bg-transparent hover:before:w-0"
              >
                Back to Sign In
              </Linker>
            </div>
          </form>
        </div>

        <div className="mt-4 text-center text-xs text-gray-400">
          © {new Date().getFullYear()} EduBahamas
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
